function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState } from 'react';
import { VerticalStepper, Button, Typography, CustomModal, PageEmptyState, Loader, PopoverOnHover, PageErrorStateIcon } from 'seagull';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import { getAgreementStatus } from "../ProposalsDashboard.utils";
import { useCopyProposalLink } from "../../../hooks/useCopyProposalLink";
import { useSendReminder } from "../../../hooks/useSendReminder";
import { useEventReporter } from "../../../event-reporter";
import { useStoreActions } from "../../../store/store-context";
import { Intent } from "../../../infra/access-control";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import ExternalLinkButton from "../../../components/ExternalLinkButton";
import { Reminders } from "./ProposalStatusRowReminders";
import styles from "./ProposalStatusLog.module.scss";
import { activityLogModel, relationshipSummaryModel, timeModel } from "../../../models";
import { RelationshipProgressStatus } from "../../../models/relationship";
import { getIntercomHelpArticle } from "../../../infra/utils";
const testPrefix = 'proposal-status-log';
export default function ProposalStatusLog(props) {
  const {
    relationshipSummary,
    dismissModal
  } = props;
  const agreementId = relationshipSummary.id;
  const {
    api
  } = useStoreActions();
  return /*#__PURE__*/React.createElement(CustomModal, {
    header: /*#__PURE__*/React.createElement("div", {
      "data-testid": "".concat(testPrefix, "-header"),
      className: styles.header
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h6",
      weight: "bold"
    }, relationshipSummaryModel.customerName(relationshipSummary), " proposal review")),
    minWidth: "916px",
    minHeight: "700px",
    "data-testid": testPrefix,
    onClose: () => dismissModal()
  }, /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: () => Promise.all([api.activityLog.get(agreementId), api.relationship.getNextReminder(agreementId).then(_ref => {
      let {
        data
      } = _ref;
      return data !== null && data !== void 0 && data.channel ? data : undefined;
    })]),
    renderChild: _ref2 => {
      let {
        data
      } = _ref2;
      return /*#__PURE__*/React.createElement(StatusLog, {
        relationshipSummary: relationshipSummary,
        activityLog: data[0],
        nextReminder: data[1]
      });
    },
    renderError: _ref3 => {
      let {
        refresh
      } = _ref3;
      return /*#__PURE__*/React.createElement(PageEmptyState, {
        "data-testid": "".concat(testPrefix, "-error"),
        illustration: /*#__PURE__*/React.createElement(PageErrorStateIcon, null),
        message: "Something went wrong. Try to refresh the data",
        actions: /*#__PURE__*/React.createElement(Button, {
          onClick: () => refresh(),
          "data-testid": "".concat(testPrefix, "-refresh-btn")
        }, "Refresh")
      });
    },
    renderLoader: () => /*#__PURE__*/React.createElement("div", {
      className: styles.loaderContainer
    }, /*#__PURE__*/React.createElement("div", {
      "data-testid": "".concat(testPrefix, "-loader"),
      className: styles.loader
    }, /*#__PURE__*/React.createElement(Loader, null), /*#__PURE__*/React.createElement(Typography, null, "Loading your status log")))
  }));
}
const StatusLog = props => {
  const {
    relationshipSummary,
    activityLog,
    nextReminder
  } = props;
  const {
    progressStatus: relationshipProgressStatus,
    progressStatusTimestamp: relationshipProgressStatusTimestamp
  } = getAgreementStatus(relationshipSummary);
  const [activePopover, setActivePopover] = useState(relationshipProgressStatus);
  const [isReminderInProgress, setIsReminderInProgress] = useState(false);
  const {
    copyAgreementLink
  } = useCopyProposalLink();
  const {
    sendReminder
  } = useSendReminder();
  const {
    reportToGtm,
    reportToAppcues
  } = useEventReporter();
  const {
    logBI
  } = useStoreActions();
  const relationshipActivityLog = activityLog === null || activityLog === void 0 ? void 0 : activityLog.filter(activityLogModel.isAgreementEntry);
  const [container, setContainer] = useState(null);
  const handleCopyToClipboard = () => {
    reportToGtm('button-click', 'vendor-copy-link-proposal', {
      progressStatus: relationshipProgressStatus
    });
    reportToAppcues('button-click', 'vendor-copy-link-proposal');
    copyAgreementLink(relationshipSummary.id, relationshipSummary.vendor.id, relationshipSummary.customer.id, relationshipProgressStatus);
  };
  const handleSendReminder = async () => {
    const eventData = {
      progressStatus: relationshipProgressStatus
    };
    reportToGtm('button-click', 'vendor-send-reminder-proposal', eventData);
    reportToAppcues('button-click', 'vendor-send-reminder-proposal');
    logBI(11302, eventData);
    await sendReminder(relationshipSummary);
    setIsReminderInProgress(true);
  };
  const debounceSetActivePopover = debounce(setActivePopover, 200);
  const onStepMouseEnter = originProgressStatus => {
    const isApproved = originProgressStatus === RelationshipProgressStatus.Approved;
    debounceSetActivePopover(isApproved ? relationshipProgressStatus : originProgressStatus);
  };
  const onStepMouseLeave = () => {
    debounceSetActivePopover(relationshipProgressStatus);
  };
  const steps = getSteps({
    relationshipProgressStatus,
    relationshipActivityLog,
    activePopover,
    onStepMouseEnter: onStepMouseEnter,
    onStepMouseLeave: onStepMouseLeave,
    lastCompletedTimestamp: relationshipProgressStatusTimestamp,
    handleCopyToClipboard,
    handleSendReminder,
    container,
    isReminderInProgress,
    nextReminder,
    isSample: relationshipSummary.isSample
  });
  return /*#__PURE__*/React.createElement("div", {
    ref: el => setContainer(el),
    "data-testid": "".concat(testPrefix, "-content"),
    className: styles.content
  }, /*#__PURE__*/React.createElement(VerticalStepper, {
    "data-testid": "stepper",
    steps: steps
  }));
};
const getSteps = _ref4 => {
  let {
    relationshipProgressStatus,
    relationshipActivityLog,
    activePopover,
    onStepMouseEnter,
    onStepMouseLeave,
    lastCompletedTimestamp,
    handleCopyToClipboard,
    handleSendReminder,
    container,
    isReminderInProgress,
    nextReminder,
    isSample
  } = _ref4;
  let progressStatusOrder = [];
  switch (relationshipProgressStatus) {
    case RelationshipProgressStatus.Sent:
    case RelationshipProgressStatus.Reviewed:
    case RelationshipProgressStatus.PaymentMethod:
    case RelationshipProgressStatus.Approved:
      progressStatusOrder = isSample ? [RelationshipProgressStatus.Sent, RelationshipProgressStatus.Reviewed, RelationshipProgressStatus.Approved] : [RelationshipProgressStatus.Sent, RelationshipProgressStatus.Reviewed, RelationshipProgressStatus.PaymentMethod, RelationshipProgressStatus.Approved];
      break;
    case RelationshipProgressStatus.MicroDeposit:
      progressStatusOrder = [RelationshipProgressStatus.Sent, RelationshipProgressStatus.Reviewed, RelationshipProgressStatus.MicroDeposit, RelationshipProgressStatus.Approved];
      break;
    case RelationshipProgressStatus.MicroDepositFailed:
      progressStatusOrder = [RelationshipProgressStatus.Sent, RelationshipProgressStatus.Reviewed, RelationshipProgressStatus.MicroDeposit, RelationshipProgressStatus.MicroDepositFailed, RelationshipProgressStatus.Approved];
  }
  const relationshipLogs = relationshipActivityLog.filter(activityLogModel.isAgreementEntry);
  const allReminders = relationshipLogs.filter(entry => activityLogModel.isAgreementEntry(entry) && activityLogModel.isReminderEntry(entry) && activityLogModel.timestamp(entry).getTime() > lastCompletedTimestamp);
  let gotCurrentStep = false;
  return progressStatusOrder.map((progressStatus, index) => {
    var _step$reminders;
    const isCurrentStep = progressStatus === relationshipProgressStatus;
    const isComplete = index === 0 || !gotCurrentStep;
    if (!gotCurrentStep && isCurrentStep) {
      gotCurrentStep = true;
    }
    const step = {
      isComplete,
      isCurrentStep,
      progressStatus,
      reminders: isCurrentStep ? allReminders : []
    };
    const completeStepEntry = relationshipLogs.find(entry => entry && activityLogModel.isAgreementEntry(entry) && activityLogModel.agreementState(entry) === step.progressStatus);
    const component = step.isComplete ? /*#__PURE__*/React.createElement(CompletedStep, {
      onStepMouseEnter: onStepMouseEnter,
      onStepMouseLeave: onStepMouseLeave,
      activePopover: activePopover,
      progressStatus: step.progressStatus,
      relationshipProgressStatus: relationshipProgressStatus,
      timestamp: completeStepEntry && activityLogModel.timestamp(completeStepEntry).getTime(),
      overrideTimestamp: step.isCurrentStep ? lastCompletedTimestamp : undefined,
      handleCopyToClipboard: handleCopyToClipboard,
      handleSendReminder: handleSendReminder,
      container: container,
      reminders: ((_step$reminders = step.reminders) !== null && _step$reminders !== void 0 ? _step$reminders : []).reverse(),
      isReminderInProgress: isReminderInProgress,
      nextReminder: step.isCurrentStep && nextReminder,
      isSample: isSample
    }) : /*#__PURE__*/React.createElement(IncompleteStep, {
      progressStatus: step.progressStatus,
      onStepMouseEnter: onStepMouseEnter,
      onStepMouseLeave: onStepMouseLeave,
      activePopover: activePopover,
      container: container
    });
    const {
      icon
    } = progressStatusToStepData[step.progressStatus];
    return {
      id: step.progressStatus,
      stepIconType: icon,
      component,
      disabled: true,
      completed: step.isComplete
    };
  });
};
const CompletedStep = _ref5 => {
  let {
    progressStatus,
    relationshipProgressStatus,
    timestamp,
    activePopover,
    onStepMouseEnter,
    onStepMouseLeave,
    overrideTimestamp,
    handleCopyToClipboard,
    handleSendReminder,
    container,
    reminders,
    isReminderInProgress,
    nextReminder,
    isSample
  } = _ref5;
  const {
    title,
    popover
  } = progressStatusToStepData[progressStatus];
  const isApproved = progressStatus === RelationshipProgressStatus.Approved;
  const isCurrentStatus = relationshipProgressStatus === progressStatus;
  return /*#__PURE__*/React.createElement(PopoverOnHover, {
    id: "popover-".concat(progressStatus),
    placement: isApproved ? 'right' : 'right-start',
    removePadding: true,
    showArrow: true,
    isOpen: activePopover === progressStatus,
    timeout: 0,
    container: container,
    trigger: triggerProps => /*#__PURE__*/React.createElement("div", {
      onMouseEnter: () => onStepMouseEnter(progressStatus),
      onMouseLeave: () => onStepMouseLeave(progressStatus),
      "data-testid": "step-".concat(progressStatus, "-completed"),
      className: cn(styles.step, styles.completedStep)
    }, /*#__PURE__*/React.createElement("div", _extends({}, triggerProps, {
      className: styles.header
    }), /*#__PURE__*/React.createElement("div", {
      "data-testid": "step-".concat(progressStatus, "-title")
    }, /*#__PURE__*/React.createElement(Typography, {
      weight: "normal"
    }, title)), /*#__PURE__*/React.createElement("div", {
      "data-testid": "step-".concat(progressStatus, "-timestamp")
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "paragraph3"
    }, (overrideTimestamp || timestamp) && timeModel.formatDateWithTime(timeModel.toDate(overrideTimestamp || timestamp))))), (reminders.length > 0 || isReminderInProgress) && isCurrentStatus ? /*#__PURE__*/React.createElement(Reminders, {
      reminders: reminders,
      progressStatus: progressStatus,
      isReminderInProgress: isReminderInProgress,
      relationshipProgressStatus: relationshipProgressStatus
    }) : null),
    content: /*#__PURE__*/React.createElement("div", {
      "data-testid": "step-".concat(progressStatus, "-popover-completed"),
      className: styles.popoverContent
    }, /*#__PURE__*/React.createElement("div", {
      "data-testid": "step-".concat(progressStatus, "-popover-completed-title")
    }, /*#__PURE__*/React.createElement(Typography, {
      weight: "normal"
    }, popover === null || popover === void 0 ? void 0 : popover.title)), /*#__PURE__*/React.createElement("div", {
      "data-testid": "step-".concat(progressStatus, "-popover-completed-description")
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "paragraph2"
    }, popover === null || popover === void 0 ? void 0 : popover.description)), nextReminder && /*#__PURE__*/React.createElement("div", {
      "data-testid": "step-".concat(progressStatus, "-popover-next-reminder")
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "paragraph2"
    }, "If the agreement is not approved by ", nextReminder.date.month, "/", nextReminder.date.day, "/", nextReminder.date.year, ", an automatic reminder will be sent by Anchor.")), popover !== null && popover !== void 0 && popover.shouldHaveActions && isCurrentStatus ? /*#__PURE__*/React.createElement("div", {
      "data-testid": "step-".concat(progressStatus, "-popover-actions"),
      className: styles.popoverActions
    }, /*#__PURE__*/React.createElement(Button, {
      intent: Intent.CopyRelationshipDirectLink,
      onClick: handleCopyToClipboard,
      skin: "primaryOutline",
      "data-testid": "step-".concat(progressStatus, "-popover-link-to-proposal")
    }, "Copy link to proposal"), /*#__PURE__*/React.createElement(Button, {
      intent: Intent.SendProposalReminder,
      disabled: isReminderInProgress || isSample,
      onClick: handleSendReminder,
      "data-testid": "step-".concat(progressStatus, "-popover-send-reminder")
    }, "Send reminder now")) : null)
  });
};
const IncompleteStep = _ref6 => {
  let {
    progressStatus,
    activePopover,
    onStepMouseEnter,
    onStepMouseLeave,
    container
  } = _ref6;
  const {
    title,
    popover
  } = progressStatusToStepData[progressStatus];
  const isApproved = progressStatus === RelationshipProgressStatus.Approved;
  const StepElement = triggerProps => /*#__PURE__*/React.createElement("div", _extends({}, triggerProps, {
    onMouseEnter: () => onStepMouseEnter(progressStatus),
    onMouseLeave: () => onStepMouseLeave(progressStatus),
    "data-testid": "step-".concat(progressStatus, "-incomplete"),
    className: cn(styles.step, styles.incompleteStep)
  }), /*#__PURE__*/React.createElement("div", {
    "data-testid": "step-".concat(progressStatus, "-title")
  }, /*#__PURE__*/React.createElement(Typography, {
    weight: "light"
  }, title)));
  return isApproved ? /*#__PURE__*/React.createElement(StepElement, null) : /*#__PURE__*/React.createElement(PopoverOnHover, {
    id: "popover-".concat(progressStatus),
    placement: "right",
    removePadding: true,
    showArrow: true,
    isOpen: activePopover === progressStatus,
    timeout: 0,
    container: container,
    trigger: StepElement,
    content: /*#__PURE__*/React.createElement("div", {
      "data-testid": "step-".concat(progressStatus, "-popover-incomplete"),
      className: styles.popoverContent
    }, /*#__PURE__*/React.createElement("div", {
      "data-testid": "step-".concat(progressStatus, "-popover-incomplete-title")
    }, /*#__PURE__*/React.createElement(Typography, null, popover === null || popover === void 0 ? void 0 : popover.title)))
  });
};
const progressStatusToStepData = {
  sent: {
    icon: 'checkmark',
    title: 'Proposal sent to client',
    popover: {
      title: 'An email with your proposal has been sent to the client',
      description: "Increase your client's approval conversion by sending the link through your own email/text.",
      shouldHaveActions: true
    }
  },
  reviewed: {
    icon: 'checkmark',
    title: 'Client reviewed proposal',
    popover: {
      title: 'The client opened and reviewed the proposal but needs to approve the terms and services',
      description: 'You can contact the client to make sure that the terms and services are as expected, and update if required.',
      shouldHaveActions: true
    }
  },
  paymentMethod: {
    icon: 'checkmark',
    title: 'Client connected payment method',
    popover: {
      title: 'The client connected a payment method but still needs to approve the agreement',
      description: 'The client is just a small step away from approving your agreement. To speed things up, send the client a link directly from you.',
      shouldHaveActions: true
    }
  },
  microDeposit: {
    icon: 'clock',
    title: 'Micro deposit initiated',
    popover: {
      title: 'The client started the micro deposit process but it hasn’t completed yet',
      description: /*#__PURE__*/React.createElement(React.Fragment, null, "Micro deposits must be verified within 10 business days. Contact your client to remind them to verify their account or, if you have access to their bank account, you may verify it on their behalf once they have approved the agreement.", ' ', /*#__PURE__*/React.createElement(ExternalLinkButton, {
        openInNewTab: true,
        href: getIntercomHelpArticle('5900687-how-can-i-manage-the-micro-deposit-process-on-behalf-of-my-client')
      }, "Learn more")),
      shouldHaveActions: true
    }
  },
  microDepositFailed: {
    icon: 'warning',
    title: 'Micro deposit failed',
    popover: {
      title: 'The client failed to validate their bank account using micro deposit',
      description: "Don't worry, customer success will help you connect your client's bank account. Contact us at CS@sayanchor.com, and we will assist you connecting your client’s account. ",
      shouldHaveActions: false
    }
  },
  approved: {
    title: 'Client approved agreement'
  }
};